import { Check, Loader2, Pencil, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

export function EditableName({
  value,
  onSave,
  display,
}: {
  value: string
  onSave: (next: string) => Promise<void>
  /**
   * What to show when nothing is stored — the composed "Unknown {n}" of a
   * manual identity. Editing still starts from the empty stored value, so the
   * placeholder can never be saved as a name (PRD-0031 slice 04).
   */
  display?: string
}) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const start = () => {
    setDraft(value)
    setError(null)
    setEditing(true)
  }
  const cancel = () => {
    setEditing(false)
    setError(null)
  }
  const commit = async () => {
    const next = draft.trim()
    if (next === value.trim() || next === '') {
      cancel()
      return
    }
    setSaving(true)
    setError(null)
    try {
      await onSave(next)
      setEditing(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('identity.detail.errors.save'))
    } finally {
      setSaving(false)
    }
  }

  if (editing) {
    return (
      <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
        <Input
          autoFocus
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') void commit()
            if (e.key === 'Escape') cancel()
          }}
          disabled={saving}
          className="text-xl font-bold tracking-tight h-auto py-1 sm:text-3xl"
        />
        <Button
          size="icon"
          variant="ghost"
          className="h-9 w-9"
          onClick={() => void commit()}
          disabled={saving}
          aria-label={t('identity.detail.save')}
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Check className="h-4 w-4 text-success" />
          )}
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-9 w-9"
          onClick={cancel}
          disabled={saving}
          aria-label={t('identity.detail.cancel')}
        >
          <X className="h-4 w-4" />
        </Button>
        {error && <p className="text-xs text-destructive ml-2">{error}</p>}
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={start}
      aria-label={t('identity.detail.editName')}
      className="group flex min-w-0 items-center gap-2 text-left rounded-md px-1 -mx-1 hover:bg-muted/40 transition-colors cursor-pointer"
    >
      <h1 className="min-w-0 break-words text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
        {value || display}
      </h1>
      <Pencil className="h-4 w-4 text-muted-foreground/60 group-hover:text-primary transition-colors max-sm:shrink-0" />
    </button>
  )
}
